import { useEffect, useMemo } from "react";

import * as Sentry from "@sentry/react";
import {
  compact,
  includes,
  isEmpty,
  map,
  some,
  sortBy,
  uniq,
  uniqBy,
} from "lodash/fp";
import { shallow } from "zustand/shallow";

import useCalendars from "@app/analytics/hooks/calendar/useCalendars";
import type { UserEventGroup } from "@app/analytics/types/eventGroupTypes";
import type { ConferencingMeetingPlatform } from "@app/common/types";

import useCalendarStore from "./useCalendarStore";
import type { CalendarFilter, ConferencingFilter } from "../types";

const filterEvents = (
  events: UserEventGroup[],
  conferencingFilter: ConferencingFilter,
  platformsToShow?: ConferencingMeetingPlatform[],
  calendarsToShow?: CalendarFilter[]
) => {
  if (isEmpty(events)) return events;

  // ensure we're only showing one of each event. last line of defense since BE should
  // only ever be returning events with unique IDs
  const uniqueEvents = uniqBy("id", events);

  if (uniqueEvents.length < events.length) {
    Sentry.captureMessage("Received multiple events with the same ID");
  }

  const filterByConferencing = conferencingFilter !== "all";
  const filterByPlatform = platformsToShow != null;
  const filterByCalendar = calendarsToShow != null;

  if (!filterByConferencing && !filterByPlatform && !filterByCalendar) {
    return uniqueEvents;
  }

  const calendarIdsToShow = map("id", calendarsToShow);

  return uniqueEvents.filter((event) => {
    const eventMeetingPlatform =
      event.meetingPlatform as ConferencingMeetingPlatform;

    const isFilteredConferencingStatus =
      !filterByConferencing ||
      (conferencingFilter === "conferencing_only"
        ? Boolean(eventMeetingPlatform)
        : !eventMeetingPlatform);

    const isFilteredPlatform =
      !filterByPlatform || platformsToShow.includes(eventMeetingPlatform);

    const isFilteredCalendar =
      !filterByCalendar ||
      some((calId) => includes(calId, calendarIdsToShow), event.calendarIds);

    return (
      isFilteredPlatform && isFilteredConferencingStatus && isFilteredCalendar
    );
  });
};

const useFilteredUpcomingEvents = (events: UserEventGroup[]) => {
  const { calendars: userCalendars } = useCalendars();

  const {
    calendars,
    conferencing,
    platforms,
    setAllCalendars,
    setAllPlatforms,
  } = useCalendarStore(
    (state) => ({
      calendars: state.calendars,
      conferencing: state.conferencing,
      platforms: state.platforms,
      setAllCalendars: state.setAllCalendars,
      setAllPlatforms: state.setAllPlatforms,
    }),
    shallow
  );

  useEffect(() => {
    const allPlatforms = uniq(compact(map("meetingPlatform", events))).sort();
    setAllPlatforms(allPlatforms as ConferencingMeetingPlatform[]);

    const allCalendars = sortBy(
      ["provider", "summary"],
      map(
        (c) => ({
          id: c.id,
          summary: c.summary,
          provider: c.provider,
        }),
        userCalendars
      )
    );
    setAllCalendars(allCalendars);
  }, [events.length, userCalendars.length]);

  const filteredEvents = useMemo(
    () =>
      filterEvents(
        events,
        conferencing,
        platforms.allSelected ? undefined : platforms.filters,
        calendars.allSelected ? undefined : calendars.filters
      ),
    [events, conferencing, calendars.filters.length, platforms.filters.length]
  );

  return filteredEvents;
};

export default useFilteredUpcomingEvents;
